import { useEffect, useCallback } from 'react';

/**
 * Tracks scroll position inside a PDF container and reports the current page
 * and scroll percentage to the parent.
 *
 * @param {Object}   options
 * @param {React.RefObject} options.containerRef - scrollable container ref
 * @param {React.RefObject} options.pageRefs     - Map ref (pageNumber -> DOM element)
 * @param {number}   options.numPages            - total number of PDF pages
 * @param {Function} options.onCurrentPageChange - callback(pageNumber)
 * @param {Function} options.onScrollPctChange   - callback(percentage 0-100)
 */
const usePdfScroll = ({
  containerRef,
  pageRefs,
  numPages,
  onCurrentPageChange,
  onScrollPctChange,
}) => {
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const maxScroll = Math.max(1, scrollHeight - clientHeight);
    const pct = Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100));
    if (onScrollPctChange) onScrollPctChange(pct);

    const anchor = container.scrollTop + container.clientHeight * 0.25;
    let closestPage = 1;
    let closestDist = Infinity;
    pageRefs.current.forEach((el, page) => {
      const dist = Math.abs(el.offsetTop - container.offsetTop - anchor);
      if (dist < closestDist) {
        closestDist = dist;
        closestPage = page;
      }
    });
    if (onCurrentPageChange) onCurrentPageChange(closestPage);
  }, [containerRef, pageRefs, onCurrentPageChange, onScrollPctChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, numPages]);
};

export default usePdfScroll;
